<template>
  <div>
    <div :class="$style.eventMenu">
      <span
        :class="itemClasses"
        :data-test-id="$testId('detailsAsideStatusMenuTitle')"
      >
        <DropDownMenu
          :classNames="dropDownMenuClassNames"
          :menuSections="dropDownMenuSections"
          :menuTitle="userStateLabel"
          :showMenuLabel="false"
        />
      </span>
    </div>
    <NextScheduledEvent
      v-if="item._id"
    />
    <NotificationInPage
      v-if="showProtip"
      :class="$style.notification"
      :data-test-id="$testId('Protip')"
      :notification="notification"
    />
    <ActivateUserModal
      v-if="showActivateUserModal"
      :data-test-id="$testId('ActivateUserModal')"
      :item="item"
      :showModal="showActivateUserModal"
      @cancel="closeActivateUserModal"
      @close="closeActivateUserModal"
      @save="handleActivateUser"
    />
    <SuspendUserModal
      v-if="showSuspendModal"
      :data-test-id="$testId('SuspendUserModal')"
      :item="item"
      :showModal="showSuspendModal"
      @cancel="closeSuspendModal"
      @close="closeSuspendModal"
      @save="handleSuspendUser"
    />
  </div>
</template>

<script>
import { ModuleConsts } from 'client/util/Constants';
import { NotificationInPage, Types } from '@jumpcloud/ui-components';
import { capitalize, get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import ActivateUserModal from 'client/systemusers/Modals/ActivateUserModal.vue';
import DropDownMenu from 'client/components/DropDownMenu/DropDownMenu.vue';
import NextScheduledEvent from 'client/systemusers/UserDetails/NextScheduledEvent.vue';
import SuspendUserModal from 'client/systemusers/Modals/SuspendUserModal.vue';
import createComputedObjectBinding from 'client/util/createComputedObjectBinding';

const alertModule = 'AlertModule';
const systemUserModel = 'SystemUserModel';
const { activated, staged, suspended } = ModuleConsts.systemUser.accountStates;
const { NotificationConfig } = Types;

export default {
  name: 'UserStateIndicator',

  components: {
    ActivateUserModal,
    DropDownMenu,
    NextScheduledEvent,
    NotificationInPage,
    SuspendUserModal,
  },

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    defaultState: '',
    itemHasBeenSaved: false,
    newMenuArray: [],
    notification: NotificationConfig({
      isMarkdown: true,
      message: '**Pro Tip:** Create users in a staged state to set up accounts before an employee\'s start date',
      type: 'informational',
    }),
    showActivateUserModal: false,
    showSuspendModal: false,
    stateChangeAction: '',
    toggleLabel: false,
  }),

  computed: {
    ...mapGetters(systemUserModel, ['displayName']),

    ...mapState('SettingsModule', {
      newSystemUserStateDefaults: (state) => state.newSystemUserStateDefaults,
    }),

    currentState() {
      const state = this.item._id
        ? get(this.item, 'state')
        : get(this.newSystemUserStateDefaults, 'manualEntry', staged);

      return state;
    },

    dropDownMenuSections() {
      const menu = [];
      const changeMenuArray = [];
      const { activate, suspend } = userStateActionType;

      const addExistingUserMenuOptions = (state) => {
        if (this.item._id) {
          this.changeStateValues(changeMenuArray, activate);
          this.changeStateValues(changeMenuArray, suspend);
        } else {
          if (state === activated) {
            this.newUserStateValues(staged, activated);
          }
          if (state === staged) {
            this.newUserStateValues(activated, staged);
          }
        }
      };

      addExistingUserMenuOptions(this.currentState);

      if (!this.item._id) {
        menu.push({ menuItems: this.newMenuArray });
      } else if (this.currentState === suspended) {
        menu.push({ menuItems: changeMenuArray });
      } else {
        menu.push({
          menuItems: changeMenuArray,
        });
      }

      return menu;
    },

    itemClasses() {
      return [
        this.$style.item,
        this.$style.dropdownItem,
        this.statusColor,
      ];
    },

    manualEntry: {
      ...createComputedObjectBinding('manualEntry'),
    },

    showProtip() {
      return !this.$route.params.id
        && this.userStateLabel === this.$options.userStates[activated];
    },

    statusColor() {
      const statusColors = {
        [activated]: this.$style.green,
        [suspended]: this.$style.gray,
        [staged]: this.$style.orange,
      };

      const state = this.toggleLabel ? this.defaultState : this.currentState;
      return statusColors[state] || this.$style.green;
    },

    userStateLabel() {
      const state = this.toggleLabel ? this.defaultState : this.currentState;
      return this.$options.userStates[state];
    },
  },

  userStates: {
    [activated]: 'Active',
    [staged]: 'Staged',
    [suspended]: 'Suspended',
  },

  created() {
    this.dropDownMenuClassNames = {
      container: 'dropDownContainerNoFlexEnd',
      dropDownList: 'statusDropDownList',
      dropDownMenuTitle: 'detailsAsideStatusMenuTitle',
    };
  },

  methods: {
    ...mapActions(alertModule, [
      'triggerErrorAlert',
      'triggerSuccessAlert',
    ]),

    ...mapActions(systemUserModel, [
      'suspendUser',
      'updateItem',
    ]),

    changeStateValues(menuItemsArray, action) {
      menuItemsArray.push({
        label: `${capitalize(action)}`,
        action: () => this.handleChangeState(action),
      });
    },

    closeActivateUserModal() {
      this.showActivateUserModal = false;
    },

    closeSuspendModal() {
      this.showSuspendModal = false;
    },

    handleActivateUser() {
      this.closeActivateUserModal();
      this.itemHasBeenSaved = true;
    },

    handleChangeDefaultState(newValue, oldValue) {
      this.toggleLabel = !this.toggleLabel;
      this.defaultState = this.toggleLabel ? newValue : oldValue;
      this.updateItem({ state: this.defaultState }).then(() => {
        // toggle label
        const label = (this.toggleLabel ? oldValue : newValue) === activated ? 'Active' : staged;
        this.newMenuArray[0].label = `${capitalize(label)}`;
      });
    },

    // Renders the activate or suspend user modal depending on the user's current state
    handleChangeState(action) {
      if (action === userStateActionType.activate) {
        this.showActivateUserModal = true;
      } else {
        this.showSuspendModal = true;
      }
    },

    handleSuspendUser() {
      this.closeSuspendModal();

      if (!this.item.samba_server_user) {
        this.suspendUser()
          .then(() => {
            this.triggerSuccessAlert(`${this.displayName} has been successfully suspended.`);
          })
          .catch(() => {
            this.triggerErrorAlert(`Error suspending ${this.displayName}.`);
          });
      }

      this.itemHasBeenSaved = true;
    },

    newUserStateValues(newValue, oldValue) {
      const state = newValue === activated ? 'Active' : staged;
      this.newMenuArray.push({
        label: `${capitalize(state)}`,
        action: () => this.handleChangeDefaultState(newValue, oldValue),
      });
    },
  },

};
</script>

<style module scoped>
.eventMenu {
  display: flex;
  justify-content: center;
  margin: var(--jc-spacer-small) 0 var(--jc-spacer-x-small);
}

.notification {
  display: flex;
  justify-content: center;
  margin: var(--jc-spacer-small) 0 var(--jc-spacer);
}

.item {
  line-height: 1.5;
  position: relative;
}

.item::before {
  content: '\2022';
  font-size: 32px;
  left: -24px;
  position: absolute;
  top: -13px;
}

/* Remove default margin-right on dropDownMenuContainer so we can center */
.item /deep/ div {
  margin: 0;
}

/*
  Override `right` setting for this menu
  Another way to do this would be to add another class to DropDownList
 */
.item /deep/ ul {
  right: -30px;
}

.dropdownItem::before {
  top: -15px;
}

.gray {
  color: var(--jcGrey300);
}

.green {
  color: var(--jcSuccess);
}

.orange {
  color: var(--jcWarning);
}
</style>
